import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

const DIMENSION_LABELS = {
  cognitive: "Perfil Cognitivo",
  operational: "Perfil Operacional",
  behavioral: "Perfil Comportamental",
  motivational: "Perfil Motivacional",
  risk: "Tolerância ao Risco",
  baggage: "Bagagem & Transferência",
};

export default function VocationalQuestion({ question, questionIndex, total, onAnswer, selectedAnswer }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const hasAnswered = useRef(false);

  useEffect(() => {
    hasAnswered.current = false;
    // Restore selected index from saved answer value
    if (selectedAnswer != null) {
      const idx = question.options.findIndex(o => o.value === selectedAnswer);
      setSelectedIndex(idx >= 0 ? idx : null);
    } else {
      setSelectedIndex(null);
    }
  }, [question?.id, selectedAnswer]);

  function handleSelect(index) {
    if (hasAnswered.current) return;
    setSelectedIndex(index);
  }

  function handleAdvance() {
    if (selectedIndex === null || hasAnswered.current) return;
    hasAnswered.current = true;
    setTimeout(() => onAnswer(question.id, question.options[selectedIndex].value), 300);
  }

  const progress = (questionIndex / total) * 100;

  return (
    <motion.div
      key={question.id}
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      className="flex flex-col items-center justify-center py-10 px-4"
    >
      <div className="max-w-lg w-full">
        {/* Progress header */}
        <div className="flex items-center justify-between mb-2.5">
          <span
            className="text-[11px] font-medium uppercase tracking-wider"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
          >
            {DIMENSION_LABELS[question.dimension]}
          </span>
          <span
            className="text-[11px] font-medium tabular-nums"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
          >
            {questionIndex + 1} de {total}
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full h-1 rounded-full mb-8" style={{ background: "#F2F4F4" }}>
          <motion.div
            className="h-full rounded-full"
            style={{ background: "#00DCA4" }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          />
        </div>

        {/* Question */}
        <h2
          className="text-lg font-semibold mb-2"
          style={{ fontFamily: "Poppins, sans-serif", color: "#003326", lineHeight: 1.4 }}
        >
          {question.text}
        </h2>
        <p
          className="text-xs mb-8"
          style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
        >
          Escolha a opção que mais se identifica com você.
        </p>

        {/* Options */}
        <div className="space-y-2">
          {question.options.map((opt, i) => {
            const isSelected = selectedIndex === i;
            return (
              <button
                key={i}
                onClick={() => handleSelect(i)}
                className="w-full flex items-center rounded-xl px-4 py-3.5 transition-all duration-200 text-left"
                style={{
                  background: isSelected ? "rgba(0, 220, 164, 0.06)" : "#ffffff",
                  border: isSelected
                    ? "1px solid #00DCA4"
                    : "1px solid #E5E9EA",
                }}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center mr-3 flex-shrink-0 transition-all duration-200"
                  style={{
                    background: isSelected ? "#00DCA4" : "#F2F4F4",
                    color: isSelected ? "#003326" : "#99A6AB",
                  }}
                >
                  <span className="text-xs font-bold">{String.fromCharCode(65 + i)}</span>
                </div>
                <span
                  className="text-sm font-medium"
                  style={{
                    fontFamily: "Work Sans, sans-serif",
                    color: isSelected ? "#003326" : "#2E3233",
                  }}
                >
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Advance button */}
        <motion.button
          onClick={handleAdvance}
          disabled={selectedIndex === null}
          className="w-full mt-8 py-3.5 rounded-full text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200"
          style={{
            fontFamily: "Work Sans, sans-serif",
            background: selectedIndex !== null ? "#00DCA4" : "#F2F4F4",
            color: selectedIndex !== null ? "#003326" : "#99A6AB",
            cursor: selectedIndex !== null ? "pointer" : "not-allowed",
          }}
          whileHover={selectedIndex !== null ? { scale: 1.01 } : {}}
          whileTap={selectedIndex !== null ? { scale: 0.99 } : {}}
        >
          {questionIndex === total - 1 ? "Finalizar" : "Avançar"}
          <ChevronRight className="w-4 h-4" />
        </motion.button>
      </div>
    </motion.div>
  );
}
